"use client"

import { Link } from "react-router-dom"
import { CheckCircle2Icon } from "lucide-react"

import { HomeTile } from "@/features/dashboard/home-d4/components/home-tile"
import { dayShortLabel, type SilentBranch } from "@/features/finance/overview/church-f3/lib/church-f3-format"
import { useLocale, useTranslations } from "@/i18n"

/** Initials for the small avatar: the first letters of the first two words. */
const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("")

/** How many silent branches fit the tile; the rest are counted. */
const SILENT_VISIBLE = 3

/**
 * Silent (F3): branches with no money recorded this period, with the date of their last entry and a way to reach
 * them (their phone when recorded, else their Branch Details). Dates are not money, but they come from the
 * church endpoint, so a reader the figures are withheld from sees the names without them.
 */
export function ChurchSilentTile({
  branches,
  datesKnown,
  detailHrefOf,
}: {
  branches: readonly SilentBranch[]
  /** Whether the last-entry dates were read (they are not for a reader the figures are withheld from). */
  datesKnown: boolean
  detailHrefOf: (branch: SilentBranch) => string | null
}) {
  const t = useTranslations("finance.overview")
  const locale = useLocale()
  const visible = branches.slice(0, SILENT_VISIBLE)

  return (
    <HomeTile title={t("widgets.churchSilent.title")} subtitle={t("widgets.churchSilent.subtitle")} testId='church-tile-silent'>
      {branches.length === 0 ? (
        <div
          data-testid='church-silent-none'
          className='flex items-start gap-2.5 rounded-xl bg-teal-50 px-3 py-2.5 text-[13px] dark:bg-teal-950/40'
        >
          <CheckCircle2Icon aria-hidden className='mt-0.5 size-4 shrink-0 text-teal-700 dark:text-teal-300' />
          <span className='flex flex-col gap-0.5'>
            <b className='text-foreground'>{t("church.silent.none.title")}</b>
            <span className='text-muted-foreground'>{t("church.silent.none.description")}</span>
          </span>
        </div>
      ) : (
        <>
          <ul className='flex flex-col divide-y divide-border/60'>
            {visible.map((branch) => {
              const href = branch.phoneNumber ? `tel:${branch.phoneNumber}` : detailHrefOf(branch)
              return (
                <li key={branch.id} data-testid='church-silent-row' className='flex items-center gap-2.5 py-2'>
                  <span
                    aria-hidden
                    className='inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  >
                    {initialsOf(branch.name)}
                  </span>
                  <span className='flex min-w-0 flex-col'>
                    <span className='truncate text-[13px] font-bold text-foreground'>{branch.name}</span>
                    <span className='truncate text-[11.5px] text-muted-foreground'>
                      {branch.lastEntryAt && branch.daysSinceLastEntry !== null
                        ? t(branch.daysSinceLastEntry === 1 ? "church.silent.lastEntryOne" : "church.silent.lastEntryOther", {
                            date: dayShortLabel(branch.lastEntryAt, locale),
                            count: branch.daysSinceLastEntry,
                          })
                        : datesKnown
                          ? t("church.silent.never")
                          : t("church.silent.noActivity")}
                    </span>
                  </span>
                  {href ? (
                    branch.phoneNumber ? (
                      <a href={href} className='ml-auto shrink-0 text-[12.5px] font-bold text-primary hover:underline'>
                        {t("church.silent.call")}
                      </a>
                    ) : (
                      <Link to={href} className='ml-auto shrink-0 text-[12.5px] font-bold text-primary hover:underline'>
                        {t("church.silent.open")}
                      </Link>
                    )
                  ) : null}
                </li>
              )
            })}
          </ul>
          {branches.length > SILENT_VISIBLE ? (
            <span className='text-xs text-muted-foreground'>
              {t(branches.length - SILENT_VISIBLE === 1 ? "church.silent.moreOne" : "church.silent.moreOther", {
                count: branches.length - SILENT_VISIBLE,
              })}
            </span>
          ) : null}
          {/* The reassurance fits only beside a short list; a long one is its own message. */}
          {branches.length < SILENT_VISIBLE ? (
            <span className='mt-auto text-xs text-muted-foreground'>{t("church.silent.note")}</span>
          ) : null}
        </>
      )}
    </HomeTile>
  )
}
